interface Props<T extends string> {
  options: readonly T[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  /**
   * Sizes each option to its own word instead of to an equal share of the
   * row. Six options stretched across a panel gives "curvy" a hundred and
   * eighty pixels of track to sit in the middle of, which reads as six empty
   * boxes rather than as a choice.
   */
  snug?: boolean;
  /**
   * How to say an option. Without it the raw identifier is printed, which is
   * fine for a set whose values are already words in the interface's language
   * and wrong for one the server stores in English.
   */
  format?: (option: T) => string;
}

/**
 * A short, fixed list of options as a row of segments.
 *
 * A three-option dropdown hides two of its three answers behind a click and
 * renders as an OS widget that looks nothing like the rest of the app.
 */
export function Segmented<T extends string>({ options, value, onChange, label, snug, format }: Props<T>) {
  return (
    <div className={`segmented ${snug ? 'segmented--snug' : ''}`} role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option}
          type="button"
          className={value === option ? 'is-on' : ''}
          aria-pressed={value === option}
          onClick={() => onChange(option)}
        >
          {format ? format(option) : option}
        </button>
      ))}
    </div>
  );
}
